import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '../components/Toast';
import { forgotPassword } from '../api/auth.api';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        document.title = 'Forgot Password | Fone Express';
    }, []);

    const validate = () => {
        if (!email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address');
            return false;
        }
        setError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            await forgotPassword({ email: email.trim() });
        } catch {
            // Silently handle — show success message regardless for security
        } finally {
            setSubmitting(false);
            setSubmitted(true);
        }
    };

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center px-4 pt-[10px] pb-16">
            <div className="w-full max-w-md">
                <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="ri-lock-password-line text-2xl text-primary"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-foreground mb-2">Forgot Password</h1>
                        <p className="text-muted-foreground text-sm">
                            Enter your email address and we'll send you a link to reset your password.
                        </p>
                    </div>

                    {submitted ? (
                        /* Success State */
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="ri-mail-check-line text-2xl text-green-600"></i>
                            </div>
                            <p className="text-foreground font-medium mb-2">Check your email</p>
                            <p className="text-muted-foreground text-sm mb-6">
                                If an account exists with this email, you'll receive a reset link.
                            </p>
                            <Link
                                to="/login"
                                className="inline-block px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                            >
                                Back to Sign In
                            </Link>
                        </div>
                    ) : (
                        /* Form */
                        <>
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                                        Email Address
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (error) setError('');
                                        }}
                                        placeholder="you@example.com"
                                        className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                            error
                                                ? 'border-red-500 focus:border-red-500'
                                                : 'border-border focus:border-primary'
                                        }`}
                                    />
                                    {error && (
                                        <p className="mt-1 text-xs text-red-500">{error}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {submitting && (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    )}
                                    {submitting ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </form>

                            <p className="mt-6 text-center text-sm text-muted-foreground">
                                Remember your password?{' '}
                                <Link to="/login" className="text-primary font-medium hover:underline">
                                    Sign In
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
